import { createFileRoute } from "@tanstack/react-router";
import {
  listParam,
  listValue,
  optionParam,
  textParam,
} from "@/utils/searchParams";
import { COVERAGE_FILTERS, type CoverageFilter } from "@/utils/toolFilters";
import { TOOL_LIFECYCLE_STATUSES } from "@/utils/toolLifecycle";
import type { ToolStatus } from "@/hooks/types";

// The search and filters live in the URL, so a trip to the importer and back
// leaves the catalogue where the admin had narrowed it (NAV-45).

/** What the coverage dropdown offers. Re-exported rather than used inline so
 *  the list the page renders and the list this route accepts are the same
 *  object — see `toolFilters.test.ts` for what drifts otherwise. */
export const CATALOGUE_COVERAGE: readonly CoverageFilter[] = COVERAGE_FILTERS;
/** The lifecycle dropdown (Jira NAV-13) — the same three transitions the row
 *  menu offers, so "show me what I have retired" narrows to exactly the set
 *  that menu can produce. `PENDING` is in neither: nothing writes it. */
export const CATALOGUE_STATUSES: readonly ToolStatus[] = TOOL_LIFECYCLE_STATUSES;

export type AdminCatalogueSearch = {
  q?: string;
  /**
   * Category *names*, comma-separated — the ones a tool document carries and
   * the dropdown shows. Data rather than a fixed list, so an unknown one
   * filters to nothing and the page says why.
   *
   * Several at once, meaning "any of these" (Jira NAV-109), and carried in the
   * same `cat` parameter it always was — so `?cat=Design`, the address this
   * filter has been sharing all along, still reads as exactly that one
   * category.
   *
   * A string rather than an array for the reason `bu` on the group's product
   * view gives: declaring an array would have the router JSON-encode it, and
   * the point of these params is a link somebody can read and edit.
   * `listParam` turns it back into names on both sides.
   */
  cat?: string;
  coverage?: CoverageFilter;
  /** Where the entry stands in its own life — not an availability, which the
   *  group states none of (ADR 0012). Nothing is hidden by default: the one
   *  page that can bring a retired entry back is the one that must show it. */
  status?: ToolStatus;
};

/** What this route will accept from the address bar.
 *
 *  Named and exported rather than written inline, so a test can hold the
 *  *route's own* answer against the lists the page hands its dropdowns. The
 *  two drifting apart is a filter that sticks in a URL and cannot be set on
 *  screen, or one only a hand-typed URL can reach — and neither is visible
 *  from either side alone. */
export function validateCatalogueSearch(
  search: Record<string, unknown>,
): AdminCatalogueSearch {
  return {
    q: textParam(search.q),
    // Normalised on the way in — deduped, emptied of blanks — so the page can
    // trust it and a hand-edited `?cat=Design,,Design` does not filter twice.
    cat: listValue(listParam(search.cat) ?? []),
    coverage: optionParam(CATALOGUE_COVERAGE, search.coverage),
    status: optionParam(CATALOGUE_STATUSES, search.status),
  };
}

export const Route = createFileRoute("/admin/catalogue")({
  validateSearch: validateCatalogueSearch,
});
